import { type ApiMetadata } from '@webapp/core/interfaces/api/ApiMetadata'
import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { type V1ApiResponse } from '@webapp/core/interfaces/api/V1ApiResponse'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IExpenseRepository } from '@webapp/modules/expense/business-logic/interfaces/IExpenseRepository'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { type ApprovableExpenseSummaryConstructor } from '@webapp/modules/expense/entities/ApprovableExpenseSummary'
import {
  ExpenseStatuses,
  type ExpenseStatusEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import { type ExpenseConstructor } from '@webapp/modules/expense/entities/Expense'
import { type ExpenseDraftConstructor } from '@webapp/modules/expense/entities/ExpenseDraft'
import { type ExpenseSummaryConstructor } from '@webapp/modules/expense/entities/ExpenseSummary'
import { type WatchlistApproverConstructor } from '@webapp/modules/expense/entities/WatchlistApprover'
import {
  type BudgetCategoryApiModel,
  type BudgetCategoryConstructorData,
  mapBudgetCategoryApiModel,
} from '@webapp/platform/budgets'
import {
  type ExpenseApiModel,
  type ExpensePermissionsApiModel,
} from '../../api-models/ExpenseApiModel'
import { type ExpenseDraftApiModel } from '../../api-models/ExpenseDraftApiModel'
import { type RequestMileageConfigurationApiModel } from '../../api-models/RequestMileageConfigurationApiModel'
import { type WatchlistApproverCountResponseApiModel } from '../../api-models/WatchlistApproverOptionApiModel'
import { mapApprovableExpenseSummaryApiModel } from '../../mappers/mapApprovableExpenseSummaryApiModel'
import { mapExpenseApiModel } from '../../mappers/mapExpenseApiModel'
import { mapExpenseDraftApiModel } from '../../mappers/mapExpenseDraftApiModel'
import {
  mapExpenseFormFieldsToCreatePayload,
  type ExpenseRequestPayload,
} from '../../mappers/mapExpenseFormFieldsToCreatePayload'
import { mapExpenseItemFormSchemaToUpdatePayload } from '../../mappers/mapExpenseItemFormSchemaToUpdatePayload'
import { mapExpenseStatusToUrl } from '../../mappers/mapExpenseStatusToUrl'
import { mapExpenseSummaryApiModel } from '../../mappers/mapExpenseSummaryApiModel'
import {
  mapRequestMileageConfigurationApiModel,
  type MileageLockSettings,
} from '../../mappers/mapRequestMileageConfigurationApiModel'
import { mapV1ExpenseRequestErrorMessage } from '../../mappers/mapV1ExpenseRequestErrorMessage'
import { mapWatchlistApproverOptionApiModel } from '../../mappers/mapWatchlistApproverOptionApiModel'
import { type ApprovableCounts } from '../../types/ApprovableCounts'
import { type ApproverIdByExpenseId } from '../../types/ApproverIdByExpenseId'
import { type BudgetCheckResult } from '../../types/BudgetCheckResult'
import { type GetApprovableExpensesResponse } from '../../types/GetApprovableExpensesResponse'
import { type GetExpenseRequestedResponse } from '../../types/GetExpenseRequestedResponse'
import { type ListApprovableExpenseSummaryParams } from '../../types/ListApprovableExpenseSummaryParams'
import { type PostApproveExpenseReportResponse } from '../../types/PostApproveExpenseReportResponse'

export type { ApprovableCounts } from '../../types/ApprovableCounts'
export type { ListApprovableExpenseSummaryParams } from '../../types/ListApprovableExpenseSummaryParams'

type V1EnvelopeWithPermissions<T> = V1ApiResponse<T> & {
  metadata?: {
    permissions: ExpensePermissionsApiModel
  }
}

interface V1ErrorData {
  __all__?: string[]
  [key: string]: string[] | undefined
}

type GetBudgetCategoriesResponse = ApiResponse<
  BudgetCategoryApiModel[],
  ApiMetadata<{ uncategorized_amount: string }>
>

type GetSpendBySecondaryCategoryResponse = ApiResponse<{
  approved: string
  invoiced: string
  pending: string
  purchased: string
  received: string
  request_pending_amount: string
}>

const throwRepoErrorFromApiV1Error = (
  error: unknown,
  extractMessage?: (data: unknown) => string | undefined
): never => {
  if (error instanceof HttpError) {
    const message = extractMessage
      ? (extractMessage(error.response) ?? '')
      : error.message
    throw new RepoError({
      code: null,
      message,
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }
  throw error
}

const throwRepoErrorFromV1ErrorBody = (error: unknown): never => {
  if (error instanceof HttpError) {
    const errorBody = error.response as { data?: V1ErrorData } | null
    const allErrors = errorBody?.data?.__all__

    throw new RepoError({
      code: null,
      message: allErrors?.[0] ?? '',
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }
  throw error
}

const throwRepoErrorFromHttpError = (error: unknown): never => {
  if (error instanceof HttpError) {
    throw new RepoError({
      code: null,
      message: String(error.message ?? ''),
      status: error.status,
      response: error.response,
      headers: error.headers,
    })
  }
  throw error
}

interface Dependency {
  httpClient: IHttpClient
}

export class ExpenseRepository implements IExpenseRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    status?: ExpenseStatusEnum,
    params?: RepoFilterParams
  ): Promise<
    RepoPaginatedResponse<
      ExpenseSummaryConstructor,
      {
        counts: {
          all: number
          pending: number
          approved: number
          denied: number
        }
      }
    >
  > {
    const res = await this.deps.httpClient.get<GetExpenseRequestedResponse>(
      `/api/v2/expenses/requested/${mapExpenseStatusToUrl(status)}/`,
      {
        params: {
          ...formatRepoFiltersToApiFilters(params),
          counts: true,
        },
      }
    )

    const expenses = res.data.data
    const metadata = res.data.metadata

    return {
      data: expenses.map((expense) => mapExpenseSummaryApiModel(expense)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
      metadata: {
        counts: {
          all: metadata?.counts?.requested?.all || 0,
          approved: metadata?.counts?.requested?.approved || 0,
          pending: metadata?.counts?.requested?.pending || 0,
          denied: metadata?.counts?.requested?.denied || 0,
        },
      },
    }
  }

  async getDrafts(): Promise<ExpenseDraftConstructor[]> {
    try {
      const res = await this.deps.httpClient.get<
        V1ApiResponse<ExpenseDraftApiModel[] | Record<string, never>>
      >('/api/expense/', {
        params: {
          status: 'draft',
        },
      })

      if (res.data.success === false) {
        const errorData = res.data.data as unknown as V1ErrorData | null
        const allErrors = errorData?.__all__

        // The V1 endpoint signals "no drafts found" with {success: false, data: {}};
        // real validation errors populate data.__all__.
        if (!allErrors?.length) {
          return []
        }

        throw new RepoError({
          code: null,
          message: allErrors[0],
          status: 400,
          response: res.data,
        })
      }

      const data = res.data.data as ExpenseDraftApiModel[]
      return (data || []).map(mapExpenseDraftApiModel)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromV1ErrorBody(err)
    }
  }

  // TODO: to be removed with PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS
  async getMileageConfiguration(): Promise<Nullable<MileageLockSettings>> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<RequestMileageConfigurationApiModel>
      >('/api/v3/kvstore/system-preferences/expense-mileage-lock/')

      return mapRequestMileageConfigurationApiModel(res.data.data)
    } catch (err) {
      // 400 from the kvstore endpoint signals "no preference set" — return null
      // so callers can treat absence as "no lock configured" rather than an error.
      if (err instanceof HttpError && err.status === 400) return null
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createExpenseRequest(
    formValues: ExpenseReportFormSchema,
    status: ExpenseStatusEnum,
    confirmedBudgetOverture: boolean
  ): Promise<{ id: number; selfApproved: boolean }> {
    try {
      const payload = mapExpenseFormFieldsToCreatePayload(formValues, status)
      const res = await this.deps.httpClient.post<
        V1ApiResponse<{ id: number; status: ExpenseStatusEnum }>,
        ExpenseRequestPayload
      >(
        `/api/request/expense/?calculate_expense=True&confirmed_budget_overture=${confirmedBudgetOverture}`,
        payload
      )

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message:
            mapV1ExpenseRequestErrorMessage(res.data) ??
            res.data.description ??
            '',
          status: 200,
          response: res.data.data,
        })
      }

      return {
        id: res.data.data.id,
        selfApproved: res.data.data.status === ExpenseStatuses.APPROVED,
      }
    } catch (err) {
      return throwRepoErrorFromApiV1Error(err, mapV1ExpenseRequestErrorMessage)
    }
  }

  async updateExpenseRequest(
    id: number,
    formValues: ExpenseReportFormSchema,
    status: ExpenseStatusEnum,
    confirmedBudgetOverture: boolean
  ): Promise<{ selfApproved: boolean }> {
    try {
      const payload = mapExpenseFormFieldsToCreatePayload(formValues, status)
      const res = await this.deps.httpClient.put<
        V1ApiResponse<{ status: ExpenseStatusEnum }>,
        ExpenseRequestPayload
      >(
        `/api/request/expense/${id}/?calculate_expense=True&confirmed_budget_overture=${confirmedBudgetOverture}`,
        payload
      )

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message:
            mapV1ExpenseRequestErrorMessage(res.data) ??
            res.data.description ??
            '',
          status: 200,
          response: null,
        })
      }

      return {
        selfApproved: res.data.data.status === ExpenseStatuses.APPROVED,
      }
    } catch (err) {
      return throwRepoErrorFromApiV1Error(err, mapV1ExpenseRequestErrorMessage)
    }
  }

  async deleteExpenseRequest(): Promise<void> {
    try {
      const res = await this.deps.httpClient.delete<
        V1ApiResponse<Record<string, never>>
      >('/api/request/expense/')

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message: res.data.description,
          status: 200,
          response: null,
        })
      }
    } catch (err) {
      return throwRepoErrorFromApiV1Error(err)
    }
  }

  async duplicate(id: number): Promise<void> {
    await this.deps.httpClient.post<
      ApiResponse<{
        uuid: string
      }>
    >(`/api/v3/expenses/${id}/replicate/`)
  }

  async sendReminder(id: number, message: Nullable<string>): Promise<void> {
    await this.deps.httpClient.post<
      ApiResponse<{
        uuid: string
      }>,
      {
        message: Nullable<string>
      }
    >(`/api/v3/expenses/${id}/remind-approver/`, {
      message: message || null,
    })
  }

  // --- ExpenseReportRepository methods ---

  async getByUuid(uuid: string): Promise<ExpenseConstructor> {
    try {
      const res = await this.deps.httpClient.get<
        V1EnvelopeWithPermissions<ExpenseApiModel[]>
      >(`/api/expense/${uuid}/`)

      if (res.data.success === false) {
        const errorData = res.data.data as unknown as V1ErrorData | null
        const allErrors = errorData?.__all__
        throw new RepoError({
          code: null,
          message: allErrors?.[0] ?? '',
          status: 400,
          response: res.data,
        })
      }

      const apiModel = res.data.data[0]
      const permissions = res.data.metadata?.permissions ?? {
        can_edit: false,
        can_delete: false,
        can_approve: false,
        can_revise: false,
      }

      return mapExpenseApiModel(apiModel, permissions)
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromV1ErrorBody(err)
    }
  }

  async approve(
    expenseId: number,
    nextApproverId: Nullable<number>
  ): Promise<void> {
    try {
      const formBody = new URLSearchParams()
      formBody.append('action', 'approve')
      formBody.append('allow_overture', 'true')
      if (nextApproverId !== null) {
        formBody.append('next_approver', String(nextApproverId))
      }

      const res = await this.deps.httpClient.post<
        V1ApiResponse<Record<string, never>>
      >(`/api/expense/approval/${expenseId}/`, formBody.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message: res.data.description,
          status: 200,
          response: null,
        })
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromV1ErrorBody(err)
    }
  }

  async deny(expenseId: number): Promise<void> {
    try {
      const formBody = new URLSearchParams()
      formBody.append('action', 'deny')
      formBody.append('allow_overture', 'true')

      const res = await this.deps.httpClient.post<
        V1ApiResponse<Record<string, never>>
      >(`/api/expense/approval/${expenseId}/`, formBody.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })

      if (res.data.success === false) {
        throw new RepoError({
          code: String(res.data.code),
          message: res.data.description,
          status: 200,
          response: null,
        })
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromV1ErrorBody(err)
    }
  }

  async update(
    expenseId: number,
    payload: { title: string; travel: Nullable<number>; notes: string }
  ): Promise<void> {
    try {
      await this.deps.httpClient.put(
        `/api/v3/expenses/${expenseId}/`,
        {
          title: payload.title,
          travel: payload.travel,
          notes: payload.notes,
        },
        {
          params: { approver: true },
        }
      )
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateItem(
    itemId: number,
    expenseReportId: number,
    item: ExpenseItemFormSchema
  ): Promise<void> {
    try {
      const payload = mapExpenseItemFormSchemaToUpdatePayload(item, {
        itemId,
        expenseReportId,
      })
      await this.deps.httpClient.put(
        `/api/v3/expenses/items/${itemId}/`,
        payload
      )
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async resubmit(
    expenseId: number,
    payload: {
      title: string
      department: number
      travel: Nullable<number>
      notes: string
      next_approver: number
    }
  ): Promise<void> {
    try {
      await this.deps.httpClient.post(
        `/api/v3/expenses/${expenseId}/resubmit/`,
        payload
      )
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteReport(expenseId: number): Promise<void> {
    try {
      const formBody = new URLSearchParams()
      formBody.append('delete', '')

      await this.deps.httpClient.post(
        `/status/expense-report/${expenseId}/`,
        formBody.toString(),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      )
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromV1ErrorBody(err)
    }
  }

  checkBudget(
    expenseId: number,
    nextApproverId: Nullable<number>
  ): Promise<BudgetCheckResult> {
    return this.checkBudgetBatch({ [String(expenseId)]: nextApproverId })
  }

  async getBudgetCategories(
    expenseId: number
  ): Promise<
    RepoPaginatedResponse<
      BudgetCategoryConstructorData,
      { uncategorizedSpend: string }
    >
  > {
    try {
      const res = await this.deps.httpClient.post<GetBudgetCategoriesResponse>(
        '/api/v3/budget-categories/search/',
        {
          order_id: expenseId,
        },
        {
          params: {
            matches_current_datetime: 'True',
          },
        }
      )

      const { data, metadata } = res.data

      return {
        data: data.map((budgetCategory) =>
          mapBudgetCategoryApiModel(budgetCategory)
        ),
        metadata: {
          uncategorizedSpend: metadata?.uncategorized_amount || '0',
        },
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getSpendBySecondaryCategory(
    secondaryCategoryId: number,
    expenseId: number,
    startDate: string,
    endDate: string
  ): Promise<
    RepoStandardResponse<{
      approved: string
      currentRequestPending: string
      invoiced: string
      pending: string
      purchased: string
      received: string
    }>
  > {
    try {
      const res =
        await this.deps.httpClient.get<GetSpendBySecondaryCategoryResponse>(
          '/api/v3/allocations-aggregate/',
          {
            params: {
              department_ids: secondaryCategoryId,
              start_date: startDate,
              end_date: endDate,
              fund_id: expenseId,
            },
          }
        )
      const { data } = res.data
      return {
        data: {
          approved: data.approved,
          currentRequestPending: data.request_pending_amount,
          invoiced: data.invoiced,
          pending: data.pending,
          purchased: data.purchased,
          received: data.received,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // --- ExpenseApprovalRepository methods ---

  async listApprovable(params: ListApprovableExpenseSummaryParams): Promise<{
    data: ApprovableExpenseSummaryConstructor[]
    metadata: {
      counts: ApprovableCounts
      pagination: RepoPaginationResponse
    }
  }> {
    try {
      const { status, subscribed, ...filterParams } = params

      const res = await this.deps.httpClient.get<GetApprovableExpensesResponse>(
        `/api/v2/expenses/approvable/${mapExpenseStatusToUrl(status)}/`,
        {
          params: {
            counts: true,
            format: 'json',
            ...formatRepoFiltersToApiFilters(filterParams),
            ...(subscribed === undefined ? {} : { subscribed }),
          },
        }
      )

      const { data, metadata } = res.data

      const countsSource = metadata?.counts?.approvable ?? metadata?.counts

      return {
        data: data.map(mapApprovableExpenseSummaryApiModel),
        metadata: {
          counts: {
            all: countsSource?.all ?? 0,
            pending: countsSource?.pending ?? 0,
            approved: countsSource?.approved ?? 0,
            denied: countsSource?.denied ?? 0,
          },
          pagination: {
            count: metadata?.pagination?.count ?? 0,
            pageSize: metadata?.pagination?.page_size ?? 10,
            numPages: metadata?.pagination?.num_pages ?? 1,
            page: metadata?.pagination?.current_page ?? 1,
          },
        },
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromHttpError(err)
    }
  }

  async checkBudgetBatch(
    approverIdByExpenseId: ApproverIdByExpenseId
  ): Promise<BudgetCheckResult> {
    try {
      const res = await this.deps.httpClient.get<BudgetCheckResult>(
        `/approve-expense-report/`,
        {
          params: {
            json: true,
            check_within_budget: true,
            approver_id_by_object_id: JSON.stringify(approverIdByExpenseId),
          },
        }
      )

      return res.data
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromHttpError(err)
    }
  }

  async batchApprove(payload: {
    approverIdByExpenseId: ApproverIdByExpenseId
    confirmOverBudgetApprove?: boolean
  }): Promise<{ success: boolean; description: string }> {
    try {
      const formBody = new URLSearchParams()
      formBody.append(
        'approver_id_by_object_id',
        JSON.stringify(payload.approverIdByExpenseId)
      )
      formBody.append('approve', 'true')
      if (payload.confirmOverBudgetApprove) {
        formBody.append('confirm_overbudget_approve', 'true')
      }

      const res =
        await this.deps.httpClient.post<PostApproveExpenseReportResponse>(
          `/approve-expense-report/`,
          formBody.toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        )

      if (res.data.success === false) {
        throw new RepoError({
          code: null,
          message: res.data.description || '',
          status: 200,
          response: res.data,
        })
      }

      return { success: res.data.success, description: res.data.description }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromHttpError(err)
    }
  }

  async listWatchlistApprovers(params: RepoFilterParams): Promise<{
    data: WatchlistApproverConstructor[]
    pagination: RepoPaginationResponse
  }> {
    try {
      const res =
        await this.deps.httpClient.get<WatchlistApproverCountResponseApiModel>(
          '/api/v2/settings/approval-subscribed/approver/count/',
          {
            params: {
              object_type: 'expense',
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const paginationRaw = res.data.metadata?.pagination
      return {
        data: (res.data.data ?? []).map(mapWatchlistApproverOptionApiModel),
        pagination: {
          count: paginationRaw?.count ?? 0,
          pageSize: paginationRaw?.page_size ?? 20,
          numPages: paginationRaw?.num_pages ?? 1,
          page: paginationRaw?.current_page ?? 1,
        },
      }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromHttpError(err)
    }
  }

  async batchDeny(payload: {
    approverIdByExpenseId: ApproverIdByExpenseId
  }): Promise<{ success: boolean; description: string }> {
    try {
      const formBody = new URLSearchParams()
      formBody.append(
        'approver_id_by_object_id',
        JSON.stringify(payload.approverIdByExpenseId)
      )
      formBody.append('deny', 'true')

      const res =
        await this.deps.httpClient.post<PostApproveExpenseReportResponse>(
          `/approve-expense-report/`,
          formBody.toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        )

      if (res.data.success === false) {
        throw new RepoError({
          code: null,
          message: res.data.description || '',
          status: 200,
          response: res.data,
        })
      }

      return { success: res.data.success, description: res.data.description }
    } catch (err) {
      if (err instanceof RepoError) throw err
      throw throwRepoErrorFromHttpError(err)
    }
  }
}
